
import React, { useState, useEffect } from 'react';
import { Card, Button, Input } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import { db, auth } from '../firebase';
import { doc, updateDoc, collection, addDoc, serverTimestamp, getDoc, onSnapshot, setDoc } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { BillingDetails } from '../types';

const Subscription = () => {
    const { userProfile, schoolProfile, currentUser, refreshProfile } = useAuth();
    const [loading, setLoading] = useState<string | null>(null);
    const navigate = useNavigate();
    const location = useLocation();

    // Determine if we are in sandbox/test mode
    const [isSandbox, setIsSandbox] = useState(() => {
        return window.location.hostname === 'localhost' ||
            window.location.hostname.includes('127.0.0.1') ||
            window.location.search.includes('sandbox=true');
    });

    // Watch for background activation (e.g. if an Admin fixes their account while they are looking at this page)
    useEffect(() => {
        if (!currentUser) return;
        const unsub = onSnapshot(doc(db, 'users', currentUser.uid), (doc) => {
            const data = doc.data();
            if (data?.isPaid === true) {
                refreshProfile();
                navigate('/dashboard');
            }
        }, (err) => {
            console.error("User doc snapshot error:", err);
        });
        return () => unsub();
    }, [currentUser, navigate, refreshProfile]);

    useEffect(() => {
        const fetchGlobalSettings = async () => {
            try {
                const docRef = doc(db, 'config', 'app_settings');
                const snap = await getDoc(docRef);
                if (snap.exists() && snap.data().stripeTestMode === true) {
                    setIsSandbox(true);
                }
            } catch (e) {
                console.log("Could not fetch global settings.");
            }
        };
        fetchGlobalSettings();

        const href = window.location.href;
        if (href.includes('success=true') || location.search.includes('success=true')) {
            navigate('/payment-success');
            return;
        }

        if (schoolProfile) {
            if (schoolProfile.isPaid === true || schoolProfile.subscriptionStatus === 'active' || schoolProfile.subscriptionStatus === 'invoice_pending') {
                navigate('/dashboard');
            }
        }
    }, [schoolProfile, userProfile, location, navigate]);

    const PRICING = [
        {
            id: 'tier_1',
            paymentLink: isSandbox
                ? 'https://buy.stripe.com/test_cNi3cueRU1ja2SC3pU2cg01'
                : 'https://buy.stripe.com/6oUcN4bFId1S64O1hM2cg04',
            name: 'Parent PRO',
            price: '£25',
            interval: '/year',
            seats: 'Full Roadmap Access',
            features: ['Access to 20+ Training Modules', 'Document Templates', 'Step-by-Step Court Roadmap', 'Live Expert Seminars', 'Support Resources'],
            isPopular: true,
        }
    ];

    const handleCardSubscribe = async (tier: any) => {
        setLoading(tier.id);
        if (!currentUser) {
            setLoading(null);
            return;
        }

        if (!tier.paymentLink) {
            alert("Payment link not configured.");
            setLoading(null);
            return;
        }

        // Using Stripe Payment Links is incredibly robust:
        // By including client_reference_id, the Firebase Stripe Extension's webhook 
        // will automatically detect the payment and create the subscription document
        // in Firestore, bypassing the need for a pre-provisioned stripeId.
        const finalUrl = `${tier.paymentLink}?prefilled_email=${encodeURIComponent(currentUser.email || '')}&client_reference_id=${currentUser.uid}`;
        window.location.href = finalUrl;
    };

    const handleLogout = async () => {
        await signOut(auth);
    };

    return (
        <div className="min-h-screen bg-slate-100 p-6 flex flex-col items-center">
            <div className="w-full max-w-6xl">
                <div className="flex justify-between items-center mb-10">
                    <div>
                        <h1 className="text-3xl font-heading font-bold text-slate-900">Select Membership</h1>
                        <p className="text-slate-500 mt-2">Invest in yourself and your children's future.</p>
                        {isSandbox && (
                            <div className="mt-2 inline-block px-3 py-1 bg-amber-100 text-amber-800 text-xs font-bold rounded-lg border border-amber-300">
                                🧪 TEST MODE: Using Sandbox Payment Links
                            </div>
                        )}
                    </div>
                    <button onClick={handleLogout} className="text-sm text-slate-500 hover:text-slate-800 font-bold uppercase tracking-widest">Sign Out</button>
                </div>

                <div className="flex flex-col items-center">
                    {PRICING.map(tier => (
                        <div key={tier.id} className="relative bg-white rounded-3xl shadow-xl overflow-hidden border-2 flex flex-col border-indigo-500 max-w-md w-full animate-slide-up">
                            <div className="bg-indigo-500 text-white text-[10px] font-black uppercase tracking-[0.2em] text-center py-2">
                                Recommended Access
                            </div>
                            <div className="p-8 flex-1">
                                <div className="flex justify-between items-start mb-2">
                                    <h3 className="text-2xl font-black text-slate-900">{tier.name}</h3>
                                    <span className="text-3xl">🎓</span>
                                </div>
                                <p className="text-slate-500 text-sm font-medium">{tier.seats}</p>
                                <div className="my-8 bg-slate-50 p-6 rounded-2xl border border-slate-100">
                                    <span className="text-5xl font-black text-slate-900">{tier.price}</span>
                                    <span className="text-slate-400 font-bold ml-1">{tier.interval}</span>
                                </div>

                                <div className="space-y-3">
                                    <Button
                                        className="w-full !py-4 text-sm font-black uppercase tracking-widest shadow-xl shadow-indigo-500/20"
                                        variant="primary"
                                        onClick={() => handleCardSubscribe(tier)}
                                        disabled={!!loading}
                                    >
                                        {loading === tier.id ? 'Connecting to Stripe...' : `Upgrade Account Now`}
                                    </Button>
                                </div>

                                <ul className="mt-8 space-y-4">
                                    {tier.features.map((feat, idx) => (
                                        <li key={idx} className="flex items-start">
                                            <span className="text-emerald-500 font-bold mr-3">✓</span>
                                            <span className="text-slate-600 text-sm font-medium">{feat}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    ))}


                </div>

                <p className="mt-12 text-center text-slate-400 text-[10px] uppercase tracking-widest font-bold">Secure checkout powered by Stripe</p>
            </div>
        </div>
    );
};

export default Subscription;
